import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { 
  Building2, Settings, BarChart3, Star, Eye, Heart, MessageSquare,
  Image, Clock, Phone, Globe, Mail, Facebook, Instagram, Twitter,
  CreditCard, Crown, Check, ChevronRight, Plus, Trash2, Save, ExternalLink,
  BadgeCheck, ShieldCheck, AlertCircle, Upload, Lock, Palette, Calendar,
  Users, Bell, BellRing, X, User, Briefcase
} from "lucide-react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { PremiumAnalytics } from "../components/AnalyticsDashboard";
import PhotoUpload from "../components/PhotoUpload";
import { getVenueImage } from "../utils/defaultImages";
import PremiumMissingNotification from "../components/PremiumMissingNotification";
import DashboardAccountTab from "../components/dashboard/DashboardAccountTab";
import DashboardBrandingTab from "../components/dashboard/DashboardBrandingTab";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Photo limits
const PHOTO_LIMITS = { free: 2, premium: 5 };

export default function DashboardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const ownerId = searchParams.get("owner");
  const paymentStatus = searchParams.get("payment");
  const sessionId = searchParams.get("session_id");
  
  const [owner, setOwner] = useState(null);
  const [venues, setVenues] = useState([]);
  const [selectedVenue, setSelectedVenue] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [verifyingEmail, setVerifyingEmail] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [pricing, setPricing] = useState({ premium_annual: 29 });
  
  const [editForm, setEditForm] = useState({
    name: "",
    description: "",
    images: [],
    structured_hours: {},
    contact_details: {},
    booking: { enabled: false },
    amenities: {},
    price_range: {},
    custom_logo_url: "",
    branding: {
      primary_color: "#1e3a5f",
      secondary_color: "#57c5b6",
      accent_color: "#159895",
      text_color: "#ffffff"
    }
  });

  // Workspace booking state
  const [workspaceSettings, setWorkspaceSettings] = useState({
    enabled: false,
    total_spaces: 10,
    notification_email: true,
    notification_app: true
  });
  const [bookings, setBookings] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [blockedFields, setBlockedFields] = useState([]);
  
  // Add New Venue state
  const [showAddVenueModal, setShowAddVenueModal] = useState(false);
  const [newVenueForm, setNewVenueForm] = useState({
    name: "",
    venue_type: "cafe",
    description: "",
    address: "",
    city: "",
    postcode: ""
  });
  const [addingVenue, setAddingVenue] = useState(false);
  
  // Owner profile editing state
  const [ownerEditForm, setOwnerEditForm] = useState({
    name: "",
    email: ""
  });
  const [savingOwner, setSavingOwner] = useState(false);
  const [showAccountTab, setShowAccountTab] = useState(false);

  // Check if owner has ANY premium venue OR premium subscription
  const hasAnyPremiumVenue = venues.some(v => v.is_premium);
  const isPremium = owner?.subscription_tier === "premium" || hasAnyPremiumVenue;
  const isVerified = owner?.email_verified;
  const photoLimit = isPremium ? PHOTO_LIMITS.premium : PHOTO_LIMITS.free;
  
  // Check if selected venue is premium
  const selectedVenueIsPremium = selectedVenue?.is_premium || owner?.subscription_tier === "premium";
  
  // Check if owner has multiple venues (for corporate CTA)
  const hasMultipleVenues = venues.length > 1;

  useEffect(() => {
    if (!ownerId) {
      navigate('/claim');
      return;
    }
    loadOwnerData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ownerId, navigate]);

  useEffect(() => {
    if (paymentStatus === "success" && sessionId) {
      pollPaymentStatus(sessionId);
    } else if (paymentStatus === "cancelled") {
      toast.info("Payment cancelled");
      navigate(`/dashboard?owner=${ownerId}`, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentStatus, sessionId, ownerId, navigate]);

  const pollPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 5) {
      toast.error("Payment verification timed out. Please refresh.");
      return;
    }
    try {
      const response = await axios.get(`${API}/checkout/status/${sid}`);
      if (response.data.payment_status === "paid") {
        toast.success("Upgraded to Premium!");
        loadOwnerData();
        navigate(`/dashboard?owner=${ownerId}`, { replace: true });
      } else {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      console.error("Payment status error:", error);
    }
  };

  const loadOwnerData = async () => {
    try {
      const [ownerRes, venuesRes, pricingRes] = await Promise.all([
        axios.get(`${API}/owners/${ownerId}`),
        axios.get(`${API}/owners/${ownerId}/venues`),
        axios.get(`${API}/pricing`).catch(() => ({ data: { premium_annual: 29 } }))
      ]);
      
      setOwner(ownerRes.data);
      setVenues(venuesRes.data);
      setPricing(pricingRes.data);
      
      // Initialize owner edit form
      setOwnerEditForm({
        name: ownerRes.data.name || "",
        email: ownerRes.data.email || ""
      });
      
      if (venuesRes.data.length > 0) {
        // Pass owner data directly since state may not be updated yet
        selectVenue(venuesRes.data[0], ownerRes.data);
      }
    } catch (error) {
      // 404 means invalid owner ID - redirect to login, don't log as error
      if (error.response?.status === 404) {
        console.warn("Owner not found - redirecting to login");
        navigate("/owner/login");
        return;
      }
      console.error("Error loading owner data:", error);
      toast.error("Failed to load dashboard");
    } finally {
      setLoading(false);
    }
  };

  const selectVenue = async (venue, ownerData = null) => {
    // Use passed owner data or fall back to state
    const currentOwner = ownerData || owner;
    setSelectedVenue(venue);
    setEditForm({
      name: venue.name || "",
      description: venue.description || "",
      images: venue.images || [],
      structured_hours: venue.structured_hours || {},
      contact_details: venue.contact_details || {},
      booking: venue.booking || { enabled: false },
      amenities: venue.amenities || {},
      price_range: venue.price_range || {},
      custom_logo_url: venue.custom_logo_url || "",
      branding: venue.branding || {
        primary_color: "#1e3a5f",
        secondary_color: "#57c5b6",
        accent_color: "#159895",
        text_color: "#ffffff"
      }
    });
    
    // Load workspace settings
    setWorkspaceSettings(venue.workspace_booking || {
      enabled: false,
      total_spaces: 10,
      notification_email: true,
      notification_app: true
    });

    // Check if this venue is premium (either venue-level or owner-level)
    const venueIsPremium = venue.is_premium || currentOwner?.subscription_tier === "premium";
    
    if (venueIsPremium) {
      try {
        const [analyticsRes, bookingsRes, notificationsRes] = await Promise.all([
          axios.get(`${API}/analytics/${venue.id}?owner_id=${ownerId}`),
          axios.get(`${API}/venues/${venue.id}/bookings?owner_id=${ownerId}&date_from=${new Date().toISOString().split('T')[0]}`).catch(() => ({ data: [] })),
          axios.get(`${API}/owners/${ownerId}/notifications?unread_only=true`).catch(() => ({ data: [] }))
        ]);
        setAnalytics(analyticsRes.data);
        setBookings(bookingsRes.data);
        setNotifications(notificationsRes.data);
      } catch (error) {
        console.error("Analytics error:", error);
      }
    }
  };

  const handleSave = async () => {
    if (!selectedVenue) return;
    setSaving(true);
    
    try {
      await axios.patch(
        `${API}/venues/${selectedVenue.id}/owner-update?owner_id=${ownerId}`,
        editForm
      );
      toast.success("Changes saved!");
      loadOwnerData();
    } catch (error) {
      console.error("Save error:", error);
      
      // Check if this is a premium_required error for contact details
      const detail = error.response?.data?.detail;
      if (detail && detail.type === "premium_required") {
        setBlockedFields(detail.blocked_fields || []);
        setShowUpgradePrompt(true);
        // Clear the blocked fields from the form
        setEditForm(prev => ({
          ...prev,
          contact_details: {}
        }));
      } else {
        toast.error(typeof detail === 'string' ? detail : "Failed to save");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleSaveWorkspaceSettings = async () => {
    if (!selectedVenue) return;
    setSaving(true);
    try {
      await axios.patch(
        `${API}/venues/${selectedVenue.id}/workspace-settings?owner_id=${ownerId}`,
        workspaceSettings
      );
      toast.success("Workspace settings saved!");
      loadOwnerData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to save workspace settings");
    } finally {
      setSaving(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!confirm("Are you sure you want to cancel this booking?")) return;
    try {
      await axios.delete(`${API}/bookings/${bookingId}?owner_id=${ownerId}`);
      toast.success("Booking cancelled");
      setBookings(bookings.filter(b => b.id !== bookingId));
    } catch (error) {
      toast.error("Failed to cancel booking");
    }
  };

  const markNotificationsRead = async () => {
    try {
      await axios.patch(`${API}/owners/${ownerId}/notifications/mark-read`);
      setNotifications([]);
    } catch (error) {
      console.error("Failed to mark notifications read");
    }
  };

  const handleAddNewVenue = async (e) => {
    e.preventDefault();
    if (!newVenueForm.name.trim() || !newVenueForm.city.trim()) {
      toast.error("Please enter venue name and city");
      return;
    }
    
    setAddingVenue(true);
    try {
      await axios.post(`${API}/owner/add-venue`, {
        owner: {
          owner_name: owner?.name || "Venue Owner",
          owner_email: owner?.email || ""
        },
        venue: {
          name: newVenueForm.name.trim(),
          venue_type: newVenueForm.venue_type,
          description: newVenueForm.description.trim(),
          location: {
            address: newVenueForm.address.trim(),
            city: newVenueForm.city.trim(),
            postcode: newVenueForm.postcode.trim()
          }
        }
      });
      toast.success("Venue submitted for approval! You'll be notified once it's approved.");
      setShowAddVenueModal(false);
      setNewVenueForm({ name: "", venue_type: "cafe", description: "", address: "", city: "", postcode: "" });
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to submit venue");
    } finally {
      setAddingVenue(false);
    }
  };

  const handleSubscribe = async (packageType, venueId = null) => {
    try {
      const response = await axios.post(`${API}/checkout/create-session`, {
        package: packageType,
        owner_id: ownerId,
        origin_url: window.location.origin,
        venue_id: venueId || selectedVenue?.id  // Premium for specific venue
      });
      window.location.href = response.data.url;
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error("Failed to start checkout");
    }
  };

  const sendVerificationCode = async () => {
    setVerifyingEmail(true);
    try {
      const response = await axios.post(`${API}/verification/send`, { owner_id: ownerId });
      if (response.data.already_verified) {
        toast.success("Email already verified!");
        loadOwnerData();
      } else {
        setShowVerification(true);
        toast.success("Verification code sent to your email!");
      }
    } catch (error) {
      toast.error("Failed to send verification code");
    } finally {
      setVerifyingEmail(false);
    }
  };

  const verifyEmail = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    setVerifyingEmail(true);
    try {
      await axios.post(`${API}/verification/verify`, { owner_id: ownerId, code: verificationCode });
      toast.success("Email verified! Your venues now show the Verified badge.");
      setShowVerification(false);
      loadOwnerData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Invalid code");
    } finally {
      setVerifyingEmail(false);
    }
  };

  const handleSaveOwnerProfile = async () => {
    if (!ownerEditForm.name.trim()) {
      toast.error("Name is required");
      return;
    }
    setSavingOwner(true);
    try {
      await axios.patch(`${API}/owners/${ownerId}/profile`, {
        name: ownerEditForm.name.trim(),
        email: ownerEditForm.email.trim()
      });
      toast.success("Profile updated!");
      loadOwnerData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : "Failed to update profile");
    } finally {
      setSavingOwner(false);
    }
  };

  const addImage = () => {
    const url = prompt("Enter image URL:");
    if (url) {
      if (editForm.images.length >= photoLimit) {
        toast.error(`Maximum ${photoLimit} photos allowed${!isPremium ? ' (upgrade for more)' : ''}`);
        return;
      }
      setEditForm({ ...editForm, images: [...editForm.images, url] });
    }
  };

  const removeImage = (index) => {
    setEditForm({ ...editForm, images: editForm.images.filter((_, i) => i !== index) });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-surface">
        <Header />
        <div className="pt-24 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-brand border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface relative">
      {/* Strider Logo Watermark */}
      <div 
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.02] pointer-events-none z-0"
        style={{
          width: '800px',
          height: '800px',
          backgroundImage: 'url(/strider-logo-bg.png)',
          backgroundSize: 'contain',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      />
      
      <Header />
      
      {/* Premium Missing Notification - Only for free venues with valid venue data */}
      {selectedVenue && !selectedVenue.is_premium && owner?.subscription_tier !== "premium" && (
        <PremiumMissingNotification 
          pricing={pricing}
          onUpgrade={() => handleSubscribe("premium_annual", selectedVenue?.id)}
          isPremium={selectedVenueIsPremium}
        />
      )}
      
      <div className="pt-16 px-4 md:px-8 pb-12 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 py-6">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Dashboard</h1>
              <p className="text-gray-500 text-sm">Welcome, {owner?.name}</p>
            </div>
            
            <div className="flex items-center gap-3">
              {isVerified ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-brand/10 text-brand rounded-lg text-sm font-medium">
                  <BadgeCheck className="w-4 h-4" />
                  Verified
                </div>
              ) : (
                <button
                  onClick={sendVerificationCode}
                  disabled={verifyingEmail}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-700 rounded-lg text-sm font-medium hover:bg-amber-200 transition-colors"
                  data-testid="verify-email-btn"
                >
                  <AlertCircle className="w-4 h-4" />
                  Verify Email
                </button>
              )}
              
              {isPremium ? (
                <div className="flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg text-sm font-medium">
                  <Crown className="w-4 h-4" />
                  Premium
                </div>
              ) : (
                <button
                  onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-brand text-white rounded-lg text-sm font-medium hover:bg-brand-dark transition-colors"
                  data-testid="upgrade-btn"
                >
                  <Crown className="w-4 h-4" />
                  Upgrade
                </button>
              )}
            </div>
          </div>

          {/* Email Verification Modal */}
          {showVerification && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <h3 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                Verify Your Email
              </h3>
              <p className="text-sm text-amber-700 mb-3">
                Enter the 6-digit code sent to {owner?.email}
              </p>
              <p className="text-xs text-amber-600 mb-3">
                Check your inbox (and spam folder). The code expires in 30 minutes.
              </p>
              <div className="flex gap-2">
                <input
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                  className="w-32 px-3 py-2 rounded-lg border border-amber-300 text-center font-mono text-lg tracking-widest"
                  data-testid="verification-code-input"
                />
                <button
                  onClick={verifyEmail}
                  disabled={verifyingEmail}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors"
                  data-testid="verify-code-btn"
                >
                  {verifyingEmail ? "Verifying..." : "Verify"}
                </button>
              </div>
            </div>
          )}

          {/* Venue Tabs */}
          <div className="mb-6 flex items-center gap-2 overflow-x-auto pb-2 hide-scrollbar">
            {venues.length > 1 && venues.map((venue) => (
              <button
                key={venue.id}
                onClick={() => selectVenue(venue)}
                className={`px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-colors ${
                  selectedVenue?.id === venue.id
                    ? "bg-brand text-white"
                    : "bg-white text-gray-700 hover:bg-gray-50 border border-gray-200"
                }`}
              >
                {venue.name}
                {venue.is_premium && <Crown className="w-3 h-3 inline ml-1 text-amber-400" />}
              </button>
            ))}
            <button
              onClick={() => setShowAddVenueModal(true)}
              className="px-4 py-2 rounded-lg whitespace-nowrap text-sm font-medium transition-colors bg-white text-brand hover:bg-brand/10 border border-brand/30 flex items-center gap-1.5"
              data-testid="add-venue-btn"
            >
              <Plus className="w-4 h-4" />
              Add Venue
            </button>
          </div>

          {selectedVenue && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Main Content */}
              <div className="lg:col-span-2">
                <Tabs defaultValue="account" className="w-full">
                  <TabsList className="w-full justify-start bg-white rounded-xl p-1 mb-4 border border-gray-100 flex-wrap">
                    <TabsTrigger value="account" className="text-sm">
                      <User className="w-4 h-4 mr-1.5" />
                      Account
                    </TabsTrigger>
                    <TabsTrigger value="details" className="text-sm">
                      <Settings className="w-4 h-4 mr-1.5" />
                      Venue
                    </TabsTrigger>
                    <TabsTrigger value="photos" className="text-sm">
                      <Image className="w-4 h-4 mr-1.5" />
                      Photos
                    </TabsTrigger>
                    <TabsTrigger value="hours" className="text-sm">
                      <Clock className="w-4 h-4 mr-1.5" />
                      Hours
                    </TabsTrigger>
                    <TabsTrigger 
                      value="analytics" 
                      className={`text-sm ${!selectedVenueIsPremium ? 'opacity-50' : ''}`}
                      disabled={!selectedVenueIsPremium}
                    >
                      <BarChart3 className="w-4 h-4 mr-1.5" />
                      Analytics
                      {!selectedVenueIsPremium && <Lock className="w-3 h-3 ml-1 text-gray-400" />}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="branding" 
                      className={`text-sm ${!selectedVenueIsPremium ? 'opacity-50' : ''}`}
                      disabled={!selectedVenueIsPremium}
                    >
                      <Palette className="w-4 h-4 mr-1.5" />
                      Branding
                      {!selectedVenueIsPremium && <Lock className="w-3 h-3 ml-1 text-gray-400" />}
                    </TabsTrigger>
                    <TabsTrigger 
                      value="workspaces" 
                      className={`text-sm relative ${!selectedVenueIsPremium ? 'opacity-50' : ''}`}
                      disabled={!selectedVenueIsPremium}
                    >
                      <Calendar className="w-4 h-4 mr-1.5" />
                      Workspaces
                      {!selectedVenueIsPremium && <Lock className="w-3 h-3 ml-1 text-gray-400" />}
                      {selectedVenueIsPremium && notifications.length > 0 && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                          {notifications.length}
                        </span>
                      )}
                    </TabsTrigger>
                  </TabsList>

                  {/* Account Tab - Owner Profile */}
                  <TabsContent value="account">
                    <DashboardAccountTab
                      owner={owner}
                      ownerEditForm={ownerEditForm}
                      setOwnerEditForm={setOwnerEditForm}
                      isVerified={isVerified}
                      sendVerificationCode={sendVerificationCode}
                      verifyingEmail={verifyingEmail}
                      handleSaveOwnerProfile={handleSaveOwnerProfile}
                      savingOwner={savingOwner}
                      venues={venues}
                      selectVenue={selectVenue}
                      setShowAddVenueModal={setShowAddVenueModal}
                      hasMultipleVenues={hasMultipleVenues}
                      isPremium={isPremium}
                      hasAnyPremiumVenue={hasAnyPremiumVenue}
                    />
                  </TabsContent>

                  {/* Details Tab */}
                  <TabsContent value="details">
                    <div className="bg-white rounded-xl p-5 border border-gray-100 space-y-5">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name</label>
                        <input
                          type="text"
                          value={editForm.name}
                          onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm"
                          data-testid="edit-name"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                          className="w-full px-3 py-2 rounded-lg border border-gray-200 focus:ring-2 focus:ring-brand/20 focus:border-brand text-sm h-24 resize-none"
                          data-testid="edit-description"
                        />
                      </div>

                      {/* Contact Details - Premium Only */}
                      {selectedVenueIsPremium ? (
                        <>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                                <Mail className="w-4 h-4" /> Email
                              </label>
                              <input
                                type="email"
                                value={editForm.contact_details?.email || ""}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  contact_details: { ...editForm.contact_details, email: e.target.value }
                                })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                data-testid="contact-email"
                              />
                            </div>
                            <div>
                              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                                <Phone className="w-4 h-4" /> Phone
                              </label>
                              <input
                                type="tel"
                                value={editForm.contact_details?.phone || ""}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  contact_details: { ...editForm.contact_details, phone: e.target.value }
                                })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                data-testid="contact-phone"
                              />
                            </div>
                            <div className="md:col-span-2">
                              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1">
                                <Globe className="w-4 h-4" /> Website
                              </label>
                              <input
                                type="url"
                                value={editForm.contact_details?.website || ""}
                                onChange={(e) => setEditForm({
                                  ...editForm,
                                  contact_details: { ...editForm.contact_details, website: e.target.value }
                                })}
                                className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                data-testid="contact-website"
                              />
                            </div>
                          </div>

                          {/* Social */}
                          <div>
                            <h3 className="text-sm font-medium text-gray-700 mb-2">Social Media</h3>
                            <div className="space-y-2">
                              {[
                                { icon: Facebook, key: "social_facebook", color: "text-blue-600", placeholder: "Facebook URL" },
                                { icon: Instagram, key: "social_instagram", color: "text-pink-600", placeholder: "Instagram URL" },
                                { icon: Twitter, key: "social_twitter", color: "text-sky-500", placeholder: "X/Twitter URL" }
                              ].map(({ icon: Icon, key, color, placeholder }) => (
                                <div key={key} className="flex items-center gap-2">
                                  <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
                                  <input
                                    type="url"
                                    placeholder={placeholder}
                                    value={editForm.contact_details?.[key] || ""}
                                    onChange={(e) => setEditForm({
                                      ...editForm,
                                      contact_details: { ...editForm.contact_details, [key]: e.target.value }
                                    })}
                                    className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                    data-testid={`contact-${key}`}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      ) : (
                        <div 
                          className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 cursor-pointer hover:shadow-md transition-shadow"
                          onClick={() => {
                            setBlockedFields(["website", "email", "phone", "social links"]);
                            setShowUpgradePrompt(true);
                          }}
                          data-testid="contact-upgrade-prompt"
                        >
                          <div className="flex items-center gap-2 text-amber-800 mb-2">
                            <Lock className="w-4 h-4" />
                            <h3 className="text-sm font-medium">Contact & Social Media</h3>
                            <span className="ml-auto text-xs bg-amber-200 text-amber-800 px-2 py-0.5 rounded-full font-medium">
                              Premium
                            </span>
                          </div>
                          <p className="text-xs text-amber-700 mb-3">
                            Want customers to contact you directly? Upgrade to Premium to add your website, phone, email, and social media links to your listing.
                          </p>
                          <button
                            type="button"
                            className="text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-lg hover:from-amber-600 hover:to-orange-600 flex items-center gap-1.5"
                          >
                            <Crown className="w-4 h-4" /> Upgrade to Premium - £{pricing?.premium_annual || 29}/year
                          </button>
                        </div>
                      )}

                      {/* Booking (Premium) */}
                      {selectedVenueIsPremium ? (
                        <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200">
                          <h3 className="text-sm font-medium text-amber-800 mb-2 flex items-center gap-1.5">
                            <Crown className="w-4 h-4" /> Booking (Premium)
                          </h3>
                          <label className="flex items-center gap-2 mb-2">
                            <input
                              type="checkbox"
                              checked={editForm.booking?.enabled || false}
                              onChange={(e) => setEditForm({
                                ...editForm,
                                booking: { ...editForm.booking, enabled: e.target.checked }
                              })}
                              className="rounded border-gray-300"
                            />
                            <span className="text-sm text-gray-700">Enable booking</span>
                          </label>
                          {editForm.booking?.enabled && (
                            <input
                              type="email"
                              placeholder="Booking email"
                              value={editForm.booking?.booking_email || ""}
                              onChange={(e) => setEditForm({
                                ...editForm,
                                booking: { ...editForm.booking, booking_email: e.target.value }
                              })}
                              className="w-full px-3 py-2 rounded-lg border border-amber-300 text-sm"
                            />
                          )}
                        </div>
                      ) : (
                        <div 
                          className="p-4 bg-gray-50 rounded-xl border border-gray-200 opacity-60 cursor-pointer hover:opacity-80 transition-opacity relative"
                          onClick={() => {
                            setBlockedFields(["booking functionality"]);
                            setShowUpgradePrompt(true);
                          }}
                          data-testid="booking-upgrade-prompt"
                        >
                          <div className="absolute top-2 right-2">
                            <Lock className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex items-center gap-2 text-gray-500 mb-2">
                            <Calendar className="w-4 h-4" />
                            <h3 className="text-sm font-medium">Booking System</h3>
                            <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                              Premium
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mb-3">
                            Let customers book workspace slots directly through your listing. Manage availability and receive instant notifications.
                          </p>
                          <div className="pointer-events-none opacity-50">
                            <label className="flex items-center gap-2 mb-2">
                              <input type="checkbox" disabled className="rounded border-gray-300" />
                              <span className="text-sm text-gray-400">Enable booking</span>
                            </label>
                          </div>
                          <button
                            type="button"
                            className="mt-2 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-lg hover:from-amber-600 hover:to-orange-600 flex items-center gap-1.5"
                          >
                            <Crown className="w-4 h-4" /> Upgrade to Unlock
                          </button>
                        </div>
                      )}

                      {/* Custom Map Pin Logo (Premium) */}
                      {selectedVenueIsPremium ? (
                        <div className="p-4 bg-gradient-to-r from-brand/5 to-brand/10 rounded-xl border border-brand/20">
                          <h3 className="text-sm font-medium text-brand mb-2 flex items-center gap-1.5">
                            <Crown className="w-4 h-4" /> Custom Map Pin Logo (Premium)
                          </h3>
                          <p className="text-xs text-gray-600 mb-2">
                            Add your logo to stand out on the map. Square images work best (max 2MB).
                          </p>
                          
                          {/* Toggle between URL and Upload */}
                          <div className="flex gap-2 mb-3">
                            <button
                              type="button"
                              onClick={() => setEditForm({ ...editForm, logo_input_mode: 'url' })}
                              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                (editForm.logo_input_mode || 'url') === 'url'
                                  ? 'bg-brand text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Enter URL
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditForm({ ...editForm, logo_input_mode: 'upload' })}
                              className={`flex-1 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                                editForm.logo_input_mode === 'upload'
                                  ? 'bg-brand text-white'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              Upload Image
                            </button>
                          </div>
                          
                          {(editForm.logo_input_mode || 'url') === 'url' ? (
                            <input
                              type="url"
                              placeholder="Logo URL (e.g., https://example.com/logo.png)"
                              value={editForm.custom_logo_url || ""}
                              onChange={(e) => setEditForm({ ...editForm, custom_logo_url: e.target.value })}
                              className="w-full px-3 py-2 rounded-lg border border-brand/30 text-sm"
                              data-testid="custom-logo-input"
                            />
                          ) : (
                            <div className="relative">
                              <input
                                type="file"
                                accept="image/*"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (!file) return;
                                  
                                  // Validate file type
                                  if (!file.type.startsWith('image/')) {
                                    toast.error("Please select an image file");
                                    return;
                                  }
                                  
                                  // Validate file size (max 2MB for logos)
                                  if (file.size > 2 * 1024 * 1024) {
                                    toast.error("Logo must be under 2MB");
                                    return;
                                  }
                                  
                                  // Convert to base64
                                  const reader = new FileReader();
                                  reader.onload = (event) => {
                                    const base64 = event.target.result;
                                    setEditForm({ ...editForm, custom_logo_url: base64 });
                                    toast.success("Logo uploaded!");
                                  };
                                  reader.onerror = () => toast.error("Failed to read file");
                                  reader.readAsDataURL(file);
                                }}
                                className="hidden"
                                id="logo-upload"
                                data-testid="custom-logo-upload"
                              />
                              <label
                                htmlFor="logo-upload"
                                className="flex items-center justify-center gap-2 w-full px-3 py-3 rounded-lg border-2 border-dashed border-brand/30 text-sm text-brand cursor-pointer hover:bg-brand/5 transition-colors"
                              >
                                <Upload className="w-4 h-4" />
                                {editForm.custom_logo_url?.startsWith('data:') ? 'Change Logo' : 'Choose Logo File'}
                              </label>
                            </div>
                          )}
                          
                          {editForm.custom_logo_url && (
                            <div className="mt-3 flex items-center gap-3">
                              <div className="w-12 h-12 rounded-full border-2 border-amber-500 overflow-hidden bg-white flex items-center justify-center shadow-md">
                                <img 
                                  src={editForm.custom_logo_url} 
                                  alt="Logo preview" 
                                  className="w-10 h-10 object-contain"
                                  onError={(e) => e.target.style.display = 'none'}
                                />
                              </div>
                              <div className="flex-1">
                                <span className="text-xs text-gray-500 block">Preview of your map pin</span>
                                <button
                                  type="button"
                                  onClick={() => setEditForm({ ...editForm, custom_logo_url: '' })}
                                  className="text-xs text-red-500 hover:text-red-600 mt-1"
                                >
                                  Remove logo
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div 
                          className="p-4 bg-gray-50 rounded-xl border border-gray-200 opacity-60 cursor-pointer hover:opacity-80 transition-opacity relative"
                          onClick={() => {
                            setBlockedFields(["custom map pin logo"]);
                            setShowUpgradePrompt(true);
                          }}
                          data-testid="logo-upgrade-prompt"
                        >
                          <div className="absolute top-2 right-2">
                            <Lock className="w-4 h-4 text-gray-400" />
                          </div>
                          <div className="flex items-center gap-2 text-gray-500 mb-2">
                            <Image className="w-4 h-4" />
                            <h3 className="text-sm font-medium">Custom Map Pin Logo</h3>
                            <span className="ml-auto text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full font-medium">
                              Premium
                            </span>
                          </div>
                          <p className="text-xs text-gray-400 mb-3">
                            Stand out on the map with your own logo. Premium venues appear with a custom branded pin that attracts more customers.
                          </p>
                          <div className="pointer-events-none opacity-50">
                            <input
                              type="url"
                              placeholder="Logo URL"
                              disabled
                              className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm bg-gray-100"
                            />
                          </div>
                          <button
                            type="button"
                            className="mt-3 text-sm font-medium text-white bg-gradient-to-r from-amber-500 to-orange-500 px-4 py-2 rounded-lg hover:from-amber-600 hover:to-orange-600 flex items-center gap-1.5"
                          >
                            <Crown className="w-4 h-4" /> Upgrade to Unlock
                          </button>
                        </div>
                      )}

                      <button onClick={handleSave} disabled={saving} className="btn-primary flex items-center gap-2" data-testid="save-btn">
                        <Save className="w-4 h-4" />
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                    </div>
                  </TabsContent>

                  {/* Photos Tab */}
                  <TabsContent value="photos">
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Upload className="w-5 h-5 text-brand" />
                          <h3 className="font-medium text-gray-900">Photo Upload</h3>
                        </div>
                      </div>
                      
                      <PhotoUpload
                        venueId={selectedVenue.id}
                        ownerId={ownerId}
                        currentPhotos={editForm.images}
                        captions={selectedVenue.image_captions || {}}
                        isPremium={selectedVenueIsPremium}
                        onPhotoChange={(newPhoto, deletedIndex) => {
                          if (newPhoto) {
                            setEditForm({ ...editForm, images: [...editForm.images, newPhoto] });
                          } else if (deletedIndex !== undefined) {
                            setEditForm({ 
                              ...editForm, 
                              images: editForm.images.filter((_, i) => i !== deletedIndex) 
                            });
                          }
                        }}
                        onCaptionChange={(index, caption) => {
                          // Update local state and reload venue
                          loadOwnerData();
                        }}
                      />
                    </div>
                  </TabsContent>

                  {/* Hours Tab */}
                  <TabsContent value="hours">
                    <div className="bg-white rounded-xl p-5 border border-gray-100">
                      <h3 className="font-medium text-gray-900 mb-4">Opening Hours</h3>
                      <div className="space-y-2">
                        {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => (
                          <div key={day} className="flex items-center gap-3">
                            <span className="w-20 text-sm font-medium capitalize text-gray-700">{day}</span>
                            <input
                              type="text"
                              placeholder="e.g., 9:00 - 17:00"
                              value={editForm.structured_hours?.[day] || ""}
                              onChange={(e) => setEditForm({
                                ...editForm,
                                structured_hours: { ...editForm.structured_hours, [day]: e.target.value }
                              })}
                              className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm"
                            />
                          </div>
                        ))}
                      </div>
                      <button onClick={handleSave} disabled={saving} className="mt-4 btn-primary flex items-center gap-2">
                        <Save className="w-4 h-4" />
                        {saving ? "Saving..." : "Save Hours"}
                      </button>
                    </div>
                  </TabsContent>

                  {/* Analytics Tab */}
                  <TabsContent value="analytics">
                    {selectedVenueIsPremium ? (
                      <div className="bg-white rounded-xl p-5 border border-gray-100">
                        <PremiumAnalytics 
                          venueId={selectedVenue.id} 
                          ownerId={ownerId} 
                        />
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl p-6 border border-gray-100">
                        <div className="text-center py-8">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <BarChart3 className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Unlock Analytics</h3>
                          <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                            See how your venue is performing with detailed analytics. Track views, favorites, reviews, and more.
                          </p>
                          
                          {/* Greyed-out preview */}
                          <div className="grid grid-cols-3 gap-4 mb-6 opacity-40 pointer-events-none">
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <Eye className="w-5 h-5 text-gray-400 mb-2" />
                              <div className="text-2xl font-bold text-gray-400">---</div>
                              <div className="text-xs text-gray-400">Views</div>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <Heart className="w-5 h-5 text-gray-400 mb-2" />
                              <div className="text-2xl font-bold text-gray-400">---</div>
                              <div className="text-xs text-gray-400">Favorites</div>
                            </div>
                            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                              <Star className="w-5 h-5 text-gray-400 mb-2" />
                              <div className="text-2xl font-bold text-gray-400">---</div>
                              <div className="text-xs text-gray-400">Rating</div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleSubscribe("premium_annual", selectedVenue?.id)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
                            data-testid="analytics-upgrade-btn"
                          >
                            <Crown className="w-5 h-5" />
                            Upgrade to Premium - £{pricing?.premium_annual || 29}/year
                          </button>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* Branding Tab */}
                  <TabsContent value="branding">
                    <DashboardBrandingTab
                      selectedVenue={selectedVenue}
                      selectedVenueIsPremium={selectedVenueIsPremium}
                      editForm={editForm}
                      setEditForm={setEditForm}
                      handleSave={handleSave}
                      saving={saving}
                      handleSubscribe={handleSubscribe}
                      pricing={pricing}
                    />
                  </TabsContent>

                  {/* Workspaces Tab */}
                  <TabsContent value="workspaces">
                    {selectedVenueIsPremium ? (
                      <div className="bg-white rounded-xl p-5 border border-gray-100 space-y-6">
                        {/* Notifications Banner */}
                        {notifications.length > 0 && (
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-2">
                              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                                <BellRing className="w-4 h-4" />
                                New Booking Notifications ({notifications.length})
                              </h4>
                              <button
                                onClick={markNotificationsRead}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Mark all as read
                              </button>
                            </div>
                            <div className="space-y-2">
                              {notifications.slice(0, 3).map((n) => (
                                <p key={n.id} className="text-sm text-blue-800">{n.message}</p>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Enable/Disable Toggle */}
                        <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div>
                            <h4 className="font-medium text-gray-900">Workspace Booking</h4>
                            <p className="text-sm text-gray-500">Allow customers to book workspace slots</p>
                          </div>
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={workspaceSettings.enabled}
                              onChange={(e) => setWorkspaceSettings({ ...workspaceSettings, enabled: e.target.checked })}
                              className="sr-only peer"
                              data-testid="workspace-enabled-toggle"
                            />
                            <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand"></div>
                          </label>
                        </div>

                        {workspaceSettings.enabled && (
                          <>
                            {/* Workspace Settings */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  <Users className="w-4 h-4 inline mr-1" />
                                  Total Available Spaces
                                </label>
                                <input
                                  type="number"
                                  min="1"
                                  max="100"
                                  value={workspaceSettings.total_spaces}
                                  onChange={(e) => setWorkspaceSettings({
                                    ...workspaceSettings,
                                    total_spaces: parseInt(e.target.value) || 10
                                  })}
                                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                                  data-testid="workspace-total-spaces"
                                />
                                <p className="text-xs text-gray-500 mt-1">Number of desks/seats available for booking</p>
                              </div>
                            </div>

                            {/* Notification Settings */}
                            <div className="border-t pt-4">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                <Bell className="w-4 h-4" />
                                Notification Preferences
                              </h4>
                              <div className="space-y-3">
                                <label className="flex items-center gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={workspaceSettings.notification_email}
                                    onChange={(e) => setWorkspaceSettings({
                                      ...workspaceSettings,
                                      notification_email: e.target.checked
                                    })}
                                    className="w-4 h-4 text-brand rounded border-gray-300 focus:ring-brand"
                                    data-testid="notification-email"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Email Notifications</span>
                                    <p className="text-xs text-gray-500">Receive an email when a booking is made</p>
                                  </div>
                                </label>
                                <label className="flex items-center gap-3 cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={workspaceSettings.notification_app}
                                    onChange={(e) => setWorkspaceSettings({
                                      ...workspaceSettings,
                                      notification_app: e.target.checked
                                    })}
                                    className="w-4 h-4 text-brand rounded border-gray-300 focus:ring-brand"
                                    data-testid="notification-app"
                                  />
                                  <div>
                                    <span className="text-sm font-medium text-gray-700">Dashboard Notifications</span>
                                    <p className="text-xs text-gray-500">See new bookings in your dashboard</p>
                                  </div>
                                </label>
                              </div>
                            </div>

                            {/* Save Settings Button */}
                            <button
                              onClick={handleSaveWorkspaceSettings}
                              disabled={saving}
                              className="btn-primary flex items-center gap-2"
                              data-testid="save-workspace-settings"
                            >
                              <Save className="w-4 h-4" />
                              {saving ? "Saving..." : "Save Workspace Settings"}
                            </button>

                            {/* Upcoming Bookings */}
                            <div className="border-t pt-4">
                              <h4 className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                <Calendar className="w-4 h-4" />
                                Upcoming Bookings
                              </h4>
                              {bookings.length === 0 ? (
                                <p className="text-sm text-gray-500 py-4 text-center">No upcoming bookings yet</p>
                              ) : (
                                <div className="space-y-2 max-h-64 overflow-y-auto">
                                  {bookings.filter(b => b.status === 'confirmed').map((booking) => (
                                    <div
                                      key={booking.id}
                                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                                    >
                                      <div>
                                        <p className="font-medium text-gray-900 text-sm">{booking.customer_name}</p>
                                        <p className="text-xs text-gray-500">
                                          {booking.date} • {booking.time_slot_label || booking.time_slot}
                                          {booking.spaces_booked > 1 && ` • ${booking.spaces_booked} spaces`}
                                        </p>
                                        <p className="text-xs text-gray-400">{booking.customer_email}</p>
                                      </div>
                                      <button
                                        onClick={() => cancelBooking(booking.id)}
                                        className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded"
                                        title="Cancel booking"
                                      >
                                        <X className="w-4 h-4" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ) : (
                      <div className="bg-white rounded-xl p-6 border border-gray-100">
                        <div className="text-center py-8">
                          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                            <Calendar className="w-8 h-8 text-gray-400" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-2">Workspace Booking System</h3>
                          <p className="text-gray-500 text-sm max-w-md mx-auto mb-6">
                            Let customers book workspace slots directly through your listing. Manage availability, receive instant notifications, and fill empty seats.
                          </p>
                          
                          {/* Greyed-out preview */}
                          <div className="max-w-md mx-auto mb-6 opacity-40 pointer-events-none">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 mb-3">
                              <div>
                                <div className="font-medium text-gray-400 text-sm">Workspace Booking</div>
                                <div className="text-xs text-gray-300">Allow customers to book slots</div>
                              </div>
                              <div className="w-10 h-5 bg-gray-300 rounded-full"></div>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                                <div className="text-xs text-gray-300">Total Spaces</div>
                                <div className="text-lg font-bold text-gray-400">10</div>
                              </div>
                              <div className="p-2 bg-gray-50 rounded border border-gray-200">
                                <div className="text-xs text-gray-300">Available Today</div>
                                <div className="text-lg font-bold text-gray-400">8</div>
                              </div>
                            </div>
                          </div>
                          
                          <button
                            onClick={() => handleSubscribe("premium_annual", selectedVenue?.id)}
                            className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-lg font-medium hover:from-amber-600 hover:to-orange-600 transition-all"
                            data-testid="workspaces-upgrade-btn"
                          >
                            <Crown className="w-5 h-5" />
                            Upgrade to Premium - £{pricing?.premium_annual || 29}/year
                          </button>
                        </div>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>
              </div>

              {/* Sidebar */}
              <div className="space-y-4">
                {/* Preview */}
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">Preview</h3>
                  <div className="rounded-lg overflow-hidden">
                    <img
                      src={getVenueImage(selectedVenue, 400)}
                      alt=""
                      className="w-full h-24 object-cover"
                    />
                    <div className="p-3 bg-gray-50">
                      <div className="flex items-center gap-2">
                        <h4 className="font-medium text-gray-900 text-sm">{selectedVenue.name}</h4>
                        {selectedVenue.is_verified && <BadgeCheck className="w-4 h-4 text-brand" />}
                      </div>
                      <p className="text-xs text-gray-500">{selectedVenue.location.city}</p>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const venueUrl = `/venue/${selectedVenue.id}`;
                      try {
                        const newWindow = window.open(venueUrl, '_blank', 'noopener,noreferrer');
                        if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
                          // Popup blocked - try direct navigation
                          window.location.href = venueUrl;
                        }
                      } catch (error) {
                        console.error("Failed to open venue:", error);
                        // Fallback to direct navigation
                        window.location.href = venueUrl;
                      }
                    }}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 text-sm text-brand hover:text-brand-dark cursor-pointer bg-transparent border-none"
                    data-testid="view-listing-btn"
                  >
                    <ExternalLink className="w-3.5 h-3.5" /> View listing
                  </button>
                </div>

                {/* Pricing - Only show if selected venue is NOT premium */}
                {!selectedVenueIsPremium && (
                  <div id="pricing" className="bg-gray-900 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-4">
                      <Crown className="w-5 h-5 text-amber-400" />
                      <h3 className="font-semibold">Go Premium</h3>
                    </div>
                    <ul className="space-y-2 mb-4 text-sm">
                      {["Featured placement", "5 photos (vs 2)", "Contact details", "Booking system", "Analytics & branding"].map((f) => (
                        <li key={`feature-${f}`} className="flex items-center gap-2 text-gray-300">
                          <Check className="w-4 h-4 text-amber-400" /> {f}
                        </li>
                      ))}
                    </ul>
                    
                    {/* Pricing Options */}
                    <div className="space-y-2 mb-3">
                      <button 
                        onClick={() => handleSubscribe("yearly")} 
                        className="w-full py-2.5 bg-amber-500 text-white rounded-lg font-medium text-sm hover:bg-amber-600 transition-colors relative" 
                        data-testid="sub-yearly"
                      >
                        <span className="absolute -top-2 -right-2 bg-green-500 text-white text-[10px] px-1.5 py-0.5 rounded-full">SAVE £31</span>
                        £{pricing.premium_annual || 149}/year
                      </button>
                      <button 
                        onClick={() => handleSubscribe("monthly")} 
                        className="w-full py-2 bg-gray-700 text-white rounded-lg font-medium text-sm hover:bg-gray-600 transition-colors" 
                        data-testid="sub-monthly"
                      >
                        £{pricing.premium_monthly || 15}/month
                      </button>
                    </div>
                    
                    {/* Voucher Code Input */}
                    <div className="border-t border-gray-700 pt-3 mt-3">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Promo code"
                          value={editForm.voucherCode || ""}
                          onChange={(e) => setEditForm({ ...editForm, voucherCode: e.target.value.toUpperCase() })}
                          className="flex-1 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-sm text-white placeholder-gray-400 focus:border-amber-400 focus:outline-none"
                          data-testid="voucher-code-input"
                        />
                        <button
                          onClick={async () => {
                            const code = editForm.voucherCode?.trim();
                            if (!code) return;
                            try {
                              const res = await axios.post(`${API}/vouchers/redeem`, {
                                code,
                                owner_id: ownerId,
                                venue_id: selectedVenue?.id
                              });
                              toast.success(res.data.message);
                              setEditForm({ ...editForm, voucherCode: "" });
                              // Refresh data
                              fetchOwnerData();
                            } catch (err) {
                              toast.error(err.response?.data?.detail || "Invalid code");
                            }
                          }}
                          disabled={!editForm.voucherCode?.trim()}
                          className="px-3 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 disabled:opacity-50 disabled:cursor-not-allowed"
                          data-testid="apply-voucher-btn"
                        >
                          Apply
                        </button>
                      </div>
                    </div>
                    
                    <p className="text-[10px] text-gray-400 mt-3 text-center">
                      Annual: 2 months free, billed yearly. Monthly: Cancel anytime. Both are recurring direct debit.
                    </p>
                  </div>
                )}
                
                {/* Premium Status - Show for premium venues */}
                {selectedVenueIsPremium && (
                  <div className="bg-gradient-to-br from-amber-500 to-orange-500 rounded-xl p-5 text-white">
                    <div className="flex items-center gap-2 mb-3">
                      <Crown className="w-5 h-5" />
                      <h3 className="font-semibold">Premium Active</h3>
                    </div>
                    <p className="text-sm text-amber-100 mb-3">
                      This venue has premium features enabled
                    </p>
                    <ul className="space-y-1 text-sm text-amber-100">
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" /> Featured placement
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" /> Contact details visible
                      </li>
                      <li className="flex items-center gap-2">
                        <Check className="w-4 h-4" /> Analytics & branding
                      </li>
                    </ul>
                  </div>
                )}

                {/* Actions */}
                <div className="bg-white rounded-xl p-4 border border-gray-100">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Actions</h3>
                  <div className="space-y-1">
                    <button onClick={() => navigate('/claim')} className="w-full flex items-center justify-between p-2.5 rounded-lg hover:bg-gray-50 text-sm text-gray-700">
                      Claim another venue <ChevronRight className="w-4 h-4 text-gray-400" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade Required Modal */}
      {showUpgradePrompt && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Crown className="w-8 h-8 text-amber-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Premium Feature</h2>
              <p className="text-gray-600 mb-4">
                Adding contact information like {blockedFields.join(", ")} is available to Premium members. 
                Upgrade now to help customers find and contact you directly!
              </p>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-4 text-left">
                <h3 className="font-medium text-gray-800 text-sm mb-2">Premium includes:</h3>
                <ul className="space-y-1.5 text-sm text-gray-600">
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Website, email & phone display
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Social media links
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> Featured placement
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> 5 photos instead of 2
                  </li>
                  <li className="flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-500" /> "Laptop Friendly" badge
                  </li>
                </ul>
              </div>
              
              <p className="text-2xl font-bold text-brand mb-4">
                £{pricing?.premium_annual || 29}<span className="text-sm font-normal text-gray-500">/year</span>
              </p>
              
              <div className="flex gap-3">
                <button
                  onClick={() => setShowUpgradePrompt(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Maybe Later
                </button>
                <button
                  onClick={() => {
                    setShowUpgradePrompt(false);
                    handleSubscribe("yearly");
                  }}
                  className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium hover:from-amber-600 hover:to-orange-600 flex items-center justify-center gap-2"
                >
                  <Crown className="w-4 h-4" /> Upgrade Now
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Add New Venue Modal */}
      {showAddVenueModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
          >
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Plus className="w-5 h-5 text-brand" />
                Add New Venue
              </h2>
              <p className="text-sm text-gray-500 mt-1">Submit a new venue for approval</p>
            </div>
            
            <form onSubmit={handleAddNewVenue} className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue Name *</label>
                <input
                  type="text"
                  value={newVenueForm.name}
                  onChange={(e) => setNewVenueForm({ ...newVenueForm, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  placeholder="e.g., The Coffee House"
                  required
                  data-testid="new-venue-name"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Venue Type</label>
                <select
                  value={newVenueForm.venue_type}
                  onChange={(e) => setNewVenueForm({ ...newVenueForm, venue_type: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                >
                  <option value="cafe">Café</option>
                  <option value="coworking">Coworking Space</option>
                  <option value="library">Library</option>
                  <option value="hotel_lobby">Hotel Lobby</option>
                  <option value="pub">Pub</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="other">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                <input
                  type="text"
                  value={newVenueForm.address}
                  onChange={(e) => setNewVenueForm({ ...newVenueForm, address: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                  placeholder="123 High Street"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                  <input
                    type="text"
                    value={newVenueForm.city}
                    onChange={(e) => setNewVenueForm({ ...newVenueForm, city: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                    placeholder="London"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <input
                    type="text"
                    value={newVenueForm.postcode}
                    onChange={(e) => setNewVenueForm({ ...newVenueForm, postcode: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm"
                    placeholder="SW1A 1AA"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newVenueForm.description}
                  onChange={(e) => setNewVenueForm({ ...newVenueForm, description: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm h-20 resize-none"
                  placeholder="Tell us about your venue..."
                />
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  Your venue will be reviewed before appearing on the site. You'll be notified once it's approved.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowAddVenueModal(false)}
                  className="flex-1 px-4 py-2.5 rounded-lg border border-gray-200 text-gray-700 font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={addingVenue}
                  className="flex-1 px-4 py-2.5 rounded-lg bg-brand text-white font-medium hover:bg-brand-dark disabled:opacity-50 flex items-center justify-center gap-2"
                  data-testid="submit-new-venue"
                >
                  {addingVenue ? "Submitting..." : "Submit Venue"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
}
